import React, { useState, useEffect } from 'react';
import { HiOutlineDocumentText } from "react-icons/hi";
import { AiOutlineInfoCircle } from "react-icons/ai";
import Pagination from "./ui/Pagination";
import { useSelector } from "react-redux";
import { selectCustomPagination } from "../features/paginationStore";
import { useQueryParams } from "../hooks/useQueryParams";
import { TailSpin } from "react-loader-spinner";
import useFetch from "../hooks/useFetch";
import { selectCid } from '../features/companyid';
import { formatDateToShortMonth } from '../utils/dateUtils';
import { toast } from 'react-toastify';
import { FaTimes } from 'react-icons/fa';


const DarkTooltip = ({ children, skills, matchScore, className }) => {
    const [visible, setVisible] = useState(false);
    const [pos, setPos] = useState({ x: 0, y: 0 });

    const handleMouseMove = (e) => {
        setPos({ x: e.clientX, y: e.clientY });
    };

    return (
        <div
            className={className || "relative flex items-center justify-center cursor-pointer"}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
            onMouseMove={handleMouseMove}
        >
            {children}
            {visible && (
                <div
                    className="fixed z-[100] p-3 rounded-lg shadow-xl pointer-events-none"
                    style={{
                        backgroundColor: "#0F172A",
                        width: "250px",
                        top: pos.y,
                        left: pos.x,
                        transform: 'translate(-50%, calc(-100% - 15px))'
                    }}
                >
                    <div className="flex justify-between items-center text-white mb-2 text-xs">
                        <span>Match Score</span>
                        <span className="font-semibold text-sm">{matchScore}</span>
                    </div>
                    <div className="flex items-start text-xs text-gray-300">
                        <span className="w-20 shrink-0">Skills matched</span>
                        <div className="flex flex-wrap gap-1">
                            {skills && skills.length > 0 ? (
                                <>
                                    {skills.slice(0, 4).map((skill, i) => (
                                        <span key={i} className="bg-white/10 px-1 py-0.5 rounded text-[10px]">{skill}</span>
                                    ))}
                                    {skills.length > 4 && (
                                        <span className="bg-white/10 px-1 py-0.5 rounded text-[10px] text-gray-400">
                                            +{skills.length - 4}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <span className="text-gray-400 italic text-[10px]">None</span>
                            )}
                        </div>
                    </div>
                    {/* Arrow */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#0F172A]"></div>
                </div>
            )}
        </div>
    );
};

const CandidateRecommendationsTable = () => {
    const pageKey = "candidate_recommendations";
    const { page, page_size } = useSelector((state) => selectCustomPagination(state, pageKey));
    const { query, setQueryParams } = useQueryParams();
    const cid = useSelector(selectCid);
    const [rejectingKey, setRejectingKey] = useState(null);
    const [addingKey, setAddingKey] = useState(null);
    const handleCandidateClick = (candidate) => {
        window.open(candidate.resume_url, "_blank");
    }

    const { send: addToApplicant } = useFetch(
        '/api/v1/internal-sourcing/add-to-applicant',
        { method: 'POST', headers: { 'Content-Type': 'application/json' } },
        false
    );

    const handleAddToApplication = async (candidate, suggestion) => {
        const key = `${candidate.candidate_uuid}_${suggestion.job_id}`;
        setAddingKey(key);
        try {
            const res = await addToApplicant({
                job_id: suggestion.job_id,
                candidate_uuid: candidate.candidate_uuid,
                company_id: cid,
            });
            if (res?.status === 'added_to_applicant') {
                toast.success('Candidate added to application');
                refetch();
            } else if (res?.error) {
                toast.error(res.error);
            }
        } catch {
            toast.error('Failed to add candidate to application');
        } finally {
            setAddingKey(null);
        }
    };

    const { send: rejectCandidate } = useFetch(
        '/api/v1/internal-sourcing/reject-candidate',
        { method: 'POST', headers: { 'Content-Type': 'application/json' } },
        false
    );

    const handleReject = async (candidateUuid, jobId) => {
        const key = `${candidateUuid}_${jobId}`;
        setRejectingKey(key);
        try {
            const res = await rejectCandidate({
                job_id: jobId,
                candidate_uuid: candidateUuid,
                company_id: cid,
            });
            if (res?.status === 'rejected') {
                toast.success('Candidate removed from recommendation');
                refetch();
            } else if (res?.error) {
                toast.error(res.error);
            }
        } catch {
            toast.error('Failed to reject candidate');
        } finally {
            setRejectingKey(null);
        }
    };

    useEffect(() => {
        if (query.page !== String(page) || query.page_size !== String(page_size)) {
            setQueryParams({ page: String(page), page_size: String(page_size) });
        }
    }, [page, page_size, query.page, query.page_size, setQueryParams]);

    const { data, isLoading, refetch } = useFetch(
        `/api/v1/internal-sourcing/recommended-candidates` +
        `?company_id=${cid}` +
        `&page=${page}` +
        `&page_size=${page_size}`,
        {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    const candidates = data?.candidates || [];
    const totalPages = data?.pagination?.total_pages || 1;

    return (
        <div className="w-full flex flex-col pt-4 mt-4 bg-white">
            {/* Header row */}
            <div className="flex items-center px-4 py-3 text-sm font-medium">
                <div className="flex items-center gap-4 w-[25%] shrink-0">
                    <span>Applicant Name</span>
                </div>
                <div className="flex items-center justify-center gap-1 w-[15%] shrink-0">
                    <span>Experience</span>
                    <div className="flex flex-col text-[8px]"></div>
                </div>
                <div className="flex items-center justify-center w-[20%] shrink-0">
                    <span>Highest Education</span>
                </div>
                <div className="flex items-center justify-center w-[20%] shrink-0">
                    <span>Location</span>
                </div>
                <div className="flex items-center justify-center gap-1 w-[20%] shrink-0">
                    <span>Upload date</span>
                    <div className="flex flex-col text-[8px]"></div>
                </div>
            </div>

            {/* List */}
            {isLoading ? (
                <div className="w-full flex justify-center py-20">
                    <TailSpin color="blue" height={60} width={60} />
                </div>
            ) : (
                <div className="flex flex-col">
                    {candidates.length > 0 ? candidates.map((candidate, index) => (
                        <div key={candidate.candidate_uuid} className="border-t border-gray-200 py-4">
                            {/* Candidate Basic Info */}
                            <div className="flex items-center px-4 mb-4 text-sm text-gray-700">
                                <div className="flex items-center gap-4 w-[25%] shrink-0 font-medium text-web-blue" onClick={() => handleCandidateClick(candidate)}>
                                    <span className="flex items-center gap-1 cursor-pointer hover:underline">{candidate.name} <HiOutlineDocumentText className="text-lg" /></span>
                                </div>
                                <div className="flex items-center justify-center w-[15%] shrink-0">
                                    {candidate.total_experience || '-'}
                                </div>
                                <div className="flex items-center justify-center w-[20%] shrink-0 text-center">
                                    {candidate.highest_education || '-'}
                                </div>
                                <div className="flex items-center justify-center w-[20%] shrink-0">
                                    {candidate.location || '-'}
                                </div>
                                <div className="flex items-center justify-center w-[20%] shrink-0">
                                    {formatDateToShortMonth(candidate.upload_date) || '-'}
                                </div>
                            </div>

                            {/* Recommendation Cards */}
                            <div className="flex gap-4 px-4 overflow-x-auto pb-4 custom-scrollbar">
                                {(candidate.top_job_matches || []).map((suggestion) => (
                                    <DarkTooltip
                                        key={suggestion.job_id}
                                        skills={suggestion.skills_matched}
                                        matchScore={`${suggestion.match_score}/10`}
                                        className="min-w-[280px] max-w-[33%] flex-1 border border-gray-200 rounded-xl p-4 bg-gray-100 flex flex-col justify-between"
                                    >
                                        <div className="flex justify-between items-start mb-1">
                                            <div className="flex flex-col text-left">
                                                <span className="font-semibold text-primaryBlack text-sm">{suggestion.job_title}</span>
                                                <span className="text-gray-400 text-xs">{candidate.current_company || ''}</span>
                                            </div>
                                            <div className="flex flex-col items-end">
                                                <div className="flex items-center gap-1 text-gray-400 text-[10px]">
                                                    <span className='w-max'>Match Score</span>
                                                    <AiOutlineInfoCircle className="text-sm cursor-pointer hover:text-gray-600" />
                                                </div>
                                                <span className={`font-bold text-lg ${suggestion.match_score >= 7 ? 'text-green-500' : suggestion.match_score >= 5 ? 'text-primaryBlack' : 'text-red-500'}`}>
                                                    {suggestion.match_score}/10
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex gap-2 mt-4 text-xs font-semibold">
                                            <button
                                                onClick={() => handleAddToApplication(candidate, suggestion)}
                                                disabled={addingKey === `${candidate.candidate_uuid}_${suggestion.job_id}`}
                                                className="flex-1 py-1.5 border border-gray-200 bg-white rounded-md text-primaryBlack hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {addingKey === `${candidate.candidate_uuid}_${suggestion.job_id}` ? (
                                                    <TailSpin color="#148EFF" height={16} width={16} />
                                                ) : 'Add To Application'}
                                            </button>
                                            <button
                                                onClick={() => handleReject(candidate.candidate_uuid, suggestion.job_id)}
                                                disabled={rejectingKey === `${candidate.candidate_uuid}_${suggestion.job_id}`}
                                                className="px-4 py-1.5 border border-gray-200 bg-white rounded-md text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {rejectingKey === `${candidate.candidate_uuid}_${suggestion.job_id}` ? (
                                                    <TailSpin color="#ef4444" height={16} width={16} />
                                                ) : <FaTimes className='w-4 h-4' />}
                                            </button>
                                        </div>
                                    </DarkTooltip>
                                ))}
                            </div>
                        </div>
                    )) : (
                        <div className="text-center py-10 text-gray-500 italic">No recommended candidates found.</div>
                    )}
                </div>
            )}

            <Pagination pageKey={pageKey} totalPages={totalPages} />
        </div>
    )
}

export default CandidateRecommendationsTable;
